import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api.js";
import { deviceDisplayId } from "../lib/deviceId.js";
import type { Customer, Endpoint, Licence } from "../lib/types.js";

function money(pence: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: (currency || "gbp").toUpperCase() }).format(pence / 100);
  } catch {
    return `£${(pence / 100).toFixed(2)}`;
  }
}
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
function billingReasonLabel(r: string | null): string {
  if (r === "subscription_create") return "New subscription";
  if (r === "subscription_cycle") return "Annual renewal";
  if (r === "subscription_update") return "Seat change";
  return r ? r.replace(/_/g, " ") : "Payment";
}
/** Serialise a CSV cell (RFC 4180 quoting) AND defuse CSV/formula injection.
 * Device name and app version come from the public activation endpoint, so a
 * value like `=HYPERLINK(...)` could run as a formula when an admin opens the
 * export in Excel/Sheets. Prefix any cell that starts with a formula trigger
 * (= + - @, or tab/CR) with an apostrophe so it's treated as text. */
function csvCell(v: string | number | null | undefined): string {
  let s = v == null ? "" : String(v);
  const risky = /^[=+\-@\t\r]/.test(s);
  if (risky) s = "'" + s;
  return risky || /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [licences, setLicences] = useState<Licence[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    if (!id) return;
    try {
      const res = await api.get<{ customer: Customer; licences: Licence[] }>(`/admin/customers/${id}`);
      setCustomer(res.customer);
      setLicences(res.licences);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load customer");
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleDelete() {
    if (!id) return;
    if (!window.confirm("Delete this customer? Their licences are revoked and removed, and every device on them is cleared from the console. This cannot be undone.")) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/admin/customers/${id}`);
      navigate("/customers");
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Failed to delete customer");
    } finally {
      setDeleting(false);
    }
  }

  const deviceCount = (licences ?? []).reduce((n, l) => n + (l.endpoints?.length ?? 0), 0);

  // Download every device on this customer's licences as a CSV, so an admin can
  // send it to the customer when they have a query. Uses the already-loaded data
  // (no extra request); the Device ID column matches what the user sees in-app.
  function exportDevices() {
    if (!customer || !licences) return;
    const header = ["Licence", "Licence status", "Device name", "Device ID", "App version", "First activated", "Last seen", "Country"];
    const rows: (string | null)[][] = [header];
    for (const lic of licences) {
      for (const e of lic.endpoints ?? []) {
        rows.push([
          lic.licence_id,
          lic.status,
          e.machine_name,
          deviceDisplayId(e.machine_id),
          e.app_version,
          e.first_seen_utc ? new Date(e.first_seen_utc).toISOString().slice(0, 10) : "",
          e.last_seen_utc ? new Date(e.last_seen_utc).toLocaleString() : "",
          e.country,
        ]);
      }
    }
    const csv = rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
    // Prepend a BOM so Excel opens the UTF-8 file with the right encoding.
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const safeName = customer.name.replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "customer";
    const a = document.createElement("a");
    a.href = url;
    a.download = `screenswytch-devices-${safeName}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (error) return <p className="error-text">{error}</p>;
  if (!customer) return <p>Loading…</p>;

  return (
    <div>
      <div className="row-between">
        <h1>{customer.name}</h1>
        <div className="button-row">
          <button onClick={exportDevices} disabled={deviceCount === 0} title="Download a CSV of this customer's devices — to send them if they have a query">
            Export devices{deviceCount ? ` (${deviceCount})` : ""}
          </button>
          <button onClick={() => setShowEditForm((v) => !v)}>{showEditForm ? "Cancel edit" : "Edit"}</button>
          <button className="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete customer"}
          </button>
        </div>
      </div>
      <p className="muted-subtitle" style={{ margin: "0 0 4px" }}>
        {customer.email}
        {customer.business_name ? ` · ${customer.business_name}` : ""}
      </p>
      {deleteError && <p className="error-text">{deleteError}</p>}

      {licences && licences.length > 0 && (() => {
        const live = licences.filter((l) => l.status !== "revoked");
        const seats = live.reduce((s, l) => s + l.seats_purchased, 0);
        const active = licences.reduce((s, l) => s + (l.seatsActive ?? 0), 0);
        const paidPence = licences.reduce((s, l) => s + (l.payments ?? []).reduce((a, p) => a + p.amount_pence, 0), 0);
        const cur = licences.flatMap((l) => l.payments ?? [])[0]?.currency ?? "gbp";
        return (
          <div className="stat-grid" style={{ margin: "16px 0 8px" }}>
            <div className="stat-card"><div className="stat-value">{seats}</div><div className="stat-label">Seats purchased</div></div>
            <div className={`stat-card ${active > seats ? "stat-warn" : ""}`}><div className="stat-value">{active}</div><div className="stat-label">Active devices</div></div>
            <div className="stat-card"><div className="stat-value">{money(paidPence, cur)}</div><div className="stat-label">Paid to date</div></div>
            <div className="stat-card"><div className="stat-value">{licences.length}</div><div className="stat-label">Licence{licences.length === 1 ? "" : "s"}</div></div>
          </div>
        );
      })()}

      {showEditForm && id && (
        <EditCustomerForm
          customerId={id}
          customer={customer}
          onSaved={() => {
            setShowEditForm(false);
            load();
          }}
        />
      )}

      <div className="row-between">
        <h2>Licences</h2>
        <button className={showIssueForm ? "secondary" : "primary"} onClick={() => setShowIssueForm((v) => !v)}>
          {showIssueForm ? "Cancel" : "Issue licence"}
        </button>
      </div>

      {showIssueForm && id && (
        <IssueLicenceForm
          customerId={id}
          defaultName={customer.name}
          defaultEmail={customer.email}
          onIssued={() => {
            setShowIssueForm(false);
            load();
          }}
        />
      )}

      {licences?.map((licence) => <LicenceCard key={licence.id} licence={licence} onChanged={load} />)}
      {licences?.length === 0 && <p>No licences yet.</p>}
    </div>
  );
}

function EditCustomerForm({
  customerId,
  customer,
  onSaved,
}: {
  customerId: string;
  customer: Customer;
  onSaved: () => void;
}) {
  const [name, setName] = useState(customer.name);
  const [email, setEmail] = useState(customer.email);
  const [businessName, setBusinessName] = useState(customer.business_name ?? "");
  const [notes, setNotes] = useState(customer.notes ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.patch(`/admin/customers/${customerId}`, {
        name,
        email,
        businessName: businessName || null,
        notes: notes || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save customer");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="card">
      <label>
        Name
        <input required value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label>
        Email
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <label>
        Business name
        <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
      </label>
      <label>
        Notes
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <button className="primary" type="submit" disabled={submitting}>
        {submitting ? "Saving…" : "Save changes"}
      </button>
      {error && <p className="error-text">{error}</p>}
    </form>
  );
}

function LicenceCard({ licence, onChanged }: { licence: Licence; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seatsActive = licence.seatsActive ?? 0;
  const overSeats = seatsActive > licence.seats_purchased;
  const boundSeats = licence.boundSeats ?? 0;
  const overBound = boundSeats > licence.seats_purchased;
  const [codeCopied, setCodeCopied] = useState(false);
  const [editingSeats, setEditingSeats] = useState(false);
  const [seatsInput, setSeatsInput] = useState(licence.seats_purchased);

  async function saveSeats() {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/admin/licences/${licence.id}`, { action: "set_seats", seatsPurchased: seatsInput });
      setEditingSeats(false);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't update seats");
    } finally {
      setBusy(false);
    }
  }

  async function copyCode() {
    if (!licence.redeem_code) return;
    await navigator.clipboard.writeText(licence.redeem_code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 1600);
  }

  async function doAction(action: "revoke" | "suspend" | "reactivate") {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/admin/licences/${licence.id}`, { action });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  // Re-send the licence-code email to the customer (recovery for a lost code).
  async function resendEmail() {
    if (!window.confirm("Re-send the licence code email to this customer?")) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.patch<{ ok: boolean; sent: boolean; loggedOnly: boolean }>(
        `/admin/licences/${licence.id}`,
        { action: "resend_email" }
      );
      window.alert(res.loggedOnly ? "Email isn't configured yet — the send was logged, not delivered." : "Licence code email sent.");
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't resend the email");
    } finally {
      setBusy(false);
    }
  }

  // Extend an invoice/PO licence's expiry (Stripe licences renew via webhook).
  // Re-signs the token to the new date so installed apps pick it up on check-in.
  async function renew() {
    const input = window.prompt("Renew to which expiry date? (YYYY-MM-DD)", licence.expiry_date);
    if (input === null) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(input.trim())) {
      setError("Enter the date as YYYY-MM-DD.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/admin/licences/${licence.id}`, { action: "renew", expiryDate: input.trim() });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Renew failed");
    } finally {
      setBusy(false);
    }
  }

  // Extend the over-allocation grace deadline (spec §3) - for a customer who's
  // genuinely over seats and needs more time before the lock kicks in.
  async function extendGrace() {
    const input = window.prompt("Extend the over-allocation grace by how many days? (1-90)", "10");
    if (input === null) return;
    const days = Number(input.trim());
    if (!Number.isInteger(days) || days < 1 || days > 90) {
      setError("Enter a whole number of days between 1 and 90.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/admin/licences/${licence.id}`, { action: "extend-grace", days });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't extend the grace period");
    } finally {
      setBusy(false);
    }
  }

  async function clearSharingFlag() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/admin/licences/${licence.id}/sharing-clear`);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  // Backfills a licence key for any licence created before licence keys existed.
  // The endpoint fills every missing code in one pass, so this fixes all of them.
  async function generateCode() {
    setBusy(true);
    setError(null);
    try {
      await api.post("/admin/licences/backfill-codes");
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't generate a licence key");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`card lic-card ${licence.sharing_flag ? "card-flagged" : ""}`}>
      <div className="lic-head">
        <span className={`status-badge status-${licence.status}`}>{licence.status}</span>
        <span className="lic-head-title">
          {licence.seats_purchased} seat{licence.seats_purchased === 1 ? "" : "s"}
        </span>
        <span className={`type-badge ${licence.stripe_subscription_id ? "type-stripe" : "type-po"}`}>
          {licence.stripe_subscription_id ? "Stripe subscription" : "Invoice / PO"}
        </span>
        <span className="lic-head-ref">Ref {licence.licence_id}</span>
      </div>

      {/* Key facts, incl. the editable seat override */}
      <div className="lic-facts">
        <div className="lic-fact">
          <span className="lic-fact-label">Seats</span>
          {editingSeats ? (
            <span className="lic-seat-edit">
              <input
                type="number"
                min={1}
                max={100000}
                value={seatsInput}
                onChange={(e) => setSeatsInput(Number(e.target.value))}
              />
              <button onClick={saveSeats} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
              <button
                className="secondary"
                onClick={() => {
                  setEditingSeats(false);
                  setSeatsInput(licence.seats_purchased);
                }}
                disabled={busy}
              >
                Cancel
              </button>
            </span>
          ) : (
            <span className="lic-fact-value">
              {licence.seats_purchased}
              <button className="link-btn" onClick={() => setEditingSeats(true)}>Edit</button>
            </span>
          )}
        </div>
        <div className="lic-fact">
          <span className="lic-fact-label">Active now</span>
          <span className={`lic-fact-value ${overSeats ? "text-danger" : ""}`}>
            {seatsActive} device{seatsActive === 1 ? "" : "s"}
            {overSeats ? " · over" : ""}
          </span>
        </div>
        <div className="lic-fact">
          <span className="lic-fact-label">Bound seats</span>
          <span className={`lic-fact-value ${overBound ? "text-danger" : ""}`}>
            {boundSeats} / {licence.seats_purchased} purchased
            {overBound ? " · over" : ""}
          </span>
        </div>
        <div className="lic-fact">
          <span className="lic-fact-label">Valid</span>
          <span className="lic-fact-value">
            {licence.start_date} → {licence.expiry_date}
          </span>
        </div>
      </div>

      {overBound && licence.overageDeadline && (
        <p className="muted-subtitle text-danger" style={{ margin: "4px 0 0" }}>
          Over-allocated — locks if still over on {fmtDate(licence.overageDeadline)}.
        </p>
      )}

      {/* Subscription & billing (Stripe) */}
      {licence.stripe_subscription_id ? (
        <div className="lic-section" style={{ marginTop: 4 }}>
          <div className="lic-key-label">Subscription &amp; billing</div>
          <div className="lic-facts" style={{ margin: "8px 0 0" }}>
            <div className="lic-fact">
              <span className="lic-fact-label">Billing status</span>
              <span className="lic-fact-value" style={{ textTransform: "capitalize" }}>{licence.billing_status ?? "—"}</span>
            </div>
            <div className="lic-fact">
              <span className="lic-fact-label">Renews</span>
              <span className="lic-fact-value">{fmtDate(licence.renews_at)}</span>
            </div>
            {(() => {
              const total = (licence.payments ?? []).reduce((s, p) => s + p.amount_pence, 0);
              const cur = licence.payments?.[0]?.currency ?? "gbp";
              return (
                <div className="lic-fact">
                  <span className="lic-fact-label">Paid to date</span>
                  <span className="lic-fact-value">{money(total, cur)}</span>
                </div>
              );
            })()}
          </div>
          {licence.pending_seats != null && (
            <p className="muted-subtitle" style={{ marginTop: 8 }}>
              Scheduled: reducing to <strong>{licence.pending_seats} seats</strong> on {fmtDate(licence.seats_effective_at)}.
            </p>
          )}
          {licence.cancel_at_period_end && (
            <p className="muted-subtitle" style={{ marginTop: 4 }}>Set to cancel at renewal ({fmtDate(licence.renews_at)}).</p>
          )}
          {licence.payments && licence.payments.length > 0 && (
            <details className="lic-details" style={{ marginTop: 8 }}>
              <summary>{licence.payments.length} payment{licence.payments.length === 1 ? "" : "s"}</summary>
              <div className="lic-details-body">
                <table className="data-table">
                  <thead><tr><th>Date</th><th>Amount</th><th>Reason</th></tr></thead>
                  <tbody>
                    {licence.payments.map((p) => (
                      <tr key={p.id}>
                        <td className="num">{fmtDate(p.paid_at)}</td>
                        <td className="num">{money(p.amount_pence, p.currency)}</td>
                        <td>{billingReasonLabel(p.billing_reason)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      ) : null}

      {/* Licence key - the thing you hand to the customer */}
      <div className="lic-section">
        {licence.redeem_code ? (
          <>
            <div className="lic-key-label">Licence key <span className="muted-subtitle">— give this to the customer</span></div>
            <div className="lic-key-row">
              <code className="lic-key">{licence.redeem_code}</code>
              <button className="secondary" onClick={copyCode}>{codeCopied ? "Copied ✓" : "Copy"}</button>
            </div>
          </>
        ) : (
          <>
            <div className="lic-key-label">Licence key</div>
            <p className="muted-subtitle" style={{ margin: "2px 0 8px" }}>
              Created before licence keys existed — generate one to give the customer.
            </p>
            <button onClick={generateCode} disabled={busy}>{busy ? "Generating…" : "Generate licence key"}</button>
          </>
        )}
      </div>

      {licence.sharing_flag && (
        <div className="warning-box">
          <p><strong>Flagged for possible key sharing</strong> (heuristic, not proof — review before revoking).</p>
          <p>{licence.sharing_reason}</p>
          <button onClick={clearSharingFlag} disabled={busy}>Clear flag</button>
        </div>
      )}

      <details className="lic-details">
        <summary>{licence.endpoints?.length ?? 0} device{(licence.endpoints?.length ?? 0) === 1 ? "" : "s"}</summary>
        <div className="lic-details-body">
          <table className="data-table">
            <thead>
              <tr>
                <th>Device name</th>
                <th>Device ID</th>
                <th>Version</th>
                <th>Last seen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {licence.endpoints?.map((e) => (
                <tr key={e.id}>
                  <td className="col-clip col-name" title={e.machine_name ?? undefined}>{e.machine_name ?? "—"}</td>
                  <td title="Matches the id shown in the user's app (Menu ▸ Licence)">
                    <code>{deviceDisplayId(e.machine_id)}</code>
                  </td>
                  <td>{e.app_version ?? "—"}</td>
                  <td className="num" title={new Date(e.last_seen_utc).toString()}>{new Date(e.last_seen_utc).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</td>
                  <td>
                    <div className="button-row">
                      <ForceDeactivateButton licenceId={licence.id} endpoint={e} onDone={onChanged} />
                      <DeviceSeatActions endpoint={e} onDone={onChanged} />
                    </div>
                  </td>
                </tr>
              ))}
              {(licence.endpoints?.length ?? 0) === 0 && (
                <tr><td colSpan={5} className="muted-subtitle">No devices activated yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </details>

      <div className="lic-actions">
        <button className="secondary" onClick={resendEmail} disabled={busy}>Resend email</button>
        <button className="secondary" onClick={renew} disabled={busy}>Renew…</button>
        {overBound && (
          <button className="secondary" onClick={extendGrace} disabled={busy}>Extend grace…</button>
        )}
        {licence.status !== "revoked" && (
          <button className="danger" onClick={() => doAction("revoke")} disabled={busy}>Revoke</button>
        )}
        {licence.status === "active" && (
          <button className="secondary" onClick={() => doAction("suspend")} disabled={busy}>Suspend</button>
        )}
        {licence.status !== "active" && (
          <button className="secondary" onClick={() => doAction("reactivate")} disabled={busy}>Reactivate</button>
        )}
      </div>
      {error && <p className="error-text">{error}</p>}
    </div>
  );
}

/** "Force-deactivate a machine" (SPEC follow-up item 1): deletes a single
 * endpoint row, immediately freeing the seat it held. Distinct from
 * revoking/suspending the whole licence - this frees exactly one seat and
 * leaves the rest of the licence untouched. */
function ForceDeactivateButton({
  licenceId,
  endpoint,
  onDone,
}: {
  licenceId: string;
  endpoint: Endpoint;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const label = endpoint.machine_name || `${endpoint.machine_id.slice(0, 12)}…`;

  async function handleClick() {
    if (!window.confirm(`Force-deactivate "${label}"? This frees the seat; the app can re-activate later as a new machine.`)) {
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/admin/licences/${licenceId}/endpoints/${endpoint.id}`);
      onDone();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "Failed to deactivate machine");
    } finally {
      setBusy(false);
    }
  }

  return (
    <button className="danger" onClick={handleClick} disabled={busy}>
      {busy ? "…" : "Force deactivate"}
    </button>
  );
}

/** "Free seat" / "Reset cooldown" (Task 8): unlike Force deactivate, these act
 * on the device itself (by machine_id, not licence+endpoint id) via
 * /admin/devices/:machineId. Free seat marks the device deactivated without
 * removing it from the console; reset cooldown clears its 24h re-activation
 * lockout early. */
function DeviceSeatActions({ endpoint, onDone }: { endpoint: Endpoint; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const label = endpoint.machine_name || `${endpoint.machine_id.slice(0, 12)}…`;

  async function freeSeat() {
    if (!window.confirm(`Free "${label}"'s seat? It stays in the console but no longer holds a seat — it can re-activate later. No cooldown is applied.`)) {
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/devices/${endpoint.machine_id}`, { action: "free-seat" });
      onDone();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "Failed to free the seat");
    } finally {
      setBusy(false);
    }
  }

  async function resetCooldown() {
    if (!window.confirm(`Reset "${label}"'s 24h re-activation cooldown?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/devices/${endpoint.machine_id}`, { action: "reset-cooldown" });
      onDone();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "Failed to reset the cooldown");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button className="secondary" onClick={freeSeat} disabled={busy}>{busy ? "…" : "Free seat"}</button>
      <button className="secondary" onClick={resetCooldown} disabled={busy}>{busy ? "…" : "Reset cooldown"}</button>
    </>
  );
}

function IssueLicenceForm({
  customerId,
  defaultName,
  defaultEmail,
  onIssued,
}: {
  customerId: string;
  defaultName: string;
  defaultEmail: string;
  onIssued: () => void;
}) {
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [seats, setSeats] = useState(10);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [expiryDate, setExpiryDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/admin/licences", {
        customerId,
        name,
        email,
        seatsPurchased: seats,
        startDate,
        expiryDate,
      });
      onIssued();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to issue licence");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="card">
      <label>
        Licensed to (name)
        <input required value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label>
        Email
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <label>
        Seats purchased
        <input
          type="number"
          min={1}
          max={100000}
          required
          value={seats}
          onChange={(e) => setSeats(Number(e.target.value))}
        />
      </label>
      <label>
        Start date
        <input type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} />
      </label>
      <label>
        Expiry date
        <input type="date" required value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
      </label>
      <button className="primary" type="submit" disabled={submitting}>
        {submitting ? "Issuing…" : "Issue licence"}
      </button>
      {error && <p className="error-text">{error}</p>}
    </form>
  );
}
